/**
 * Money Utility Functions
 *
 * This module provides utility functions for working with money values in the application.
 * It includes functions for creating, converting, formatting, and performing arithmetic
 * operations on money models.
 */

import currencyJs from 'currency.js'

import type { MoneyModel } from '@/domains/pricing/types/models/MoneyModel.types'
import type {
  AbsParams,
  AddParams,
  AllocateParams,
  CompareParams,
  ConvertToScaleParams,
  DistributeParams,
  DivideParams,
  EqualsParams,
  FormatAmountParams,
  FormatForLocaleParams,
  FormatMajorAmountParams,
  FormatManyParams,
  FormatMinorAmountParams,
  FormatMinorOrDashParams,
  FormatOptions,
  FormatOrDashParams,
  FormatOrZeroParams,
  FormatParams,
  FormatWithCodeParams,
  FormatWithSymbolParams,
  FormatZeroAmountParams,
  FromMajorParams,
  FromMinorParams,
  FromMinorStringParams,
  IsNegativeParams,
  IsPositiveParams,
  IsValidMoneyParams,
  IsZeroParams,
  MultiplyByRatioParams,
  MultiplyParams,
  PercentOfParams,
  RoundToCurrencyScaleParams,
  SafeSumParams,
  SubtractParams,
  SumParams,
  ToMajorParams,
  ToMinorParams,
  ToMinorStringParams,
  WithFormattedParams,
  ZeroParams
} from '@/utils/money-util/MoneyUtil.types'

import { DEFAULT_NEW_MONEY } from '@/domains/pricing/types/models/MoneyModel.types'
import { PLACEHOLDERS } from '@/types/const/placeholders'
import { getCurrencyScale } from '@/utils/currency-util/currencyUtil'

/**
 * Gets the default currency and scale values
 *
 * @param currencyCode - Optional currency code to override default
 * @param scale - Optional scale to override default
 * @returns An object with currency and scale properties
 */
const getDefaultValues = (currencyCode?: string, scale?: number): { currency: string; scale: number } => {
  const defaultMoney = DEFAULT_NEW_MONEY()
  return {
    currency: currencyCode ?? defaultMoney.currency,
    scale: scale ?? defaultMoney.scale
  }
}

/**
 * Creates a currency.js object from a major value with the specified currency and scale
 *
 * @param majorValue - The major value to convert
 * @param currency - The currency code
 * @param scale - The scale/precision
 * @returns A currency.js object
 */
const createCurrencyObject = (majorValue: number, currency: string, scale: number): currencyJs => {
  const factory = getCurrencyFactory(currency, scale)
  return factory(majorValue)
}

/**
 * Cache of currency factory functions to improve performance
 * Key format: `${currencyCode}-${scale}`
 */
const currencyFactories = new Map<string, (value: number) => currencyJs>()

/**
 * Mapping of currency codes to their symbols
 */
export const CURRENCY_SYMBOLS = {
  EUR: '€',
  USD: '$',
  GBP: '£',
  JPY: '¥',
  CHF: 'CHF',
  CAD: 'C$'
} as const

/**
 * Locale-specific formatting configurations
 * Contains decimal and thousand separator characters for different locales
 */
const LOCALE_FORMATS = {
  'en-US': { decimal: '.', separator: ',' },
  'en-GB': { decimal: '.', separator: ',' },
  'de-DE': { decimal: ',', separator: '.' },
  'fr-FR': { decimal: ',', separator: ' ' },
  'es-ES': { decimal: ',', separator: '.' },
  'it-IT': { decimal: ',', separator: '.' },
  'nl-NL': { decimal: ',', separator: '.' }
} as const

/**
 * Gets or creates a currency factory function for the specified currency code and scale
 *
 * @param currencyCode - The currency code (e.g., 'USD', 'EUR')
 * @param scale - The number of decimal places for the currency
 * @returns A factory function that creates currency.js instances with the specified configuration
 */
const getCurrencyFactory = (currencyCode: string, scale: number) => {
  const key = `${currencyCode}-${scale}`

  const currencyOptions: currencyJs.Options = {
    precision: scale,
    fromCents: false,
    errorOnInvalid: false,
    increment: Math.pow(10, -scale),
    symbol: '',
    separator: '',
    decimal: '.'
  }

  if (currencyFactories.has(key)) {
    return currencyFactories.get(key)!
  }

  /**
   * Handles currency.js rounding inconsistency for negative numbers.
   * The library's "round half up" method rounds -0.14835 to -0.1483 (towards zero)
   * instead of -0.1484 (away from zero) as expected in financial calculations.
   * This factory adjusts negative number rounding to be consistent with positive numbers.
   * @param majorValue - The major currency value to convert
   */
  const factory = (majorValue: number) => {
    // If the number is negative, we need to adjust the rounding logic.
    if (majorValue < 0) {
      // 1. Take the absolute value to ensure rounding happens away from zero.
      //    For example, 0.14835 will be correctly rounded to 0.1484.
      const absoluteValue = Math.abs(majorValue)

      // 2. Create a currency instance with the positive, rounded value.
      const positiveRoundedCurrency = currencyJs(absoluteValue, currencyOptions)

      // 3. Multiply by -1 to reapply the negative sign.
      //    This returns a new currency object with the value -0.1484.
      return positiveRoundedCurrency.multiply(-1)
    }
    return currencyJs(majorValue, currencyOptions)
  }

  currencyFactories.set(key, factory)
  return factory
}

/**
 * Gets the locale-specific formatting configuration
 *
 * @param locale - The locale identifier (e.g., 'en-US', 'de-DE')
 * @returns The locale configuration or null if not found
 */
const getLocaleConfig = (locale: string) => {
  const localeFormat = LOCALE_FORMATS[locale as keyof typeof LOCALE_FORMATS]
  if (!localeFormat) return null

  return localeFormat
}

/**
 * Creates a formatting configuration object for currency.js based on money model and options
 *
 * @param money - The money model to format
 * @param options - Formatting options
 * @returns A configuration object for currency.js formatting
 */
const createFormatConfig = (money: MoneyModel, options: FormatOptions) => {
  const precision = options.precision ?? money.scale
  const useGrouping = options.useGrouping ?? true
  const localeConfig = options.locale ? getLocaleConfig(options.locale) : null

  if (localeConfig) {
    return {
      precision,
      decimal: options.decimal ?? localeConfig.decimal,
      separator: useGrouping ? (options.separator ?? localeConfig.separator) : '',
      symbol: getSymbolForStyle(money.currency, options),
      fromCents: false,
      errorOnInvalid: false
    }
  }

  return {
    precision,
    decimal: options.decimal ?? '.',
    separator: useGrouping ? (options.separator ?? ',') : '',
    symbol: getSymbolForStyle(money.currency, options),
    fromCents: false,
    errorOnInvalid: false
  }
}

/**
 * Determines the currency symbol to use based on formatting options
 *
 * @param currencyCode - The currency code (e.g., 'USD', 'EUR')
 * @param options - Formatting options
 * @returns The appropriate currency symbol based on the style option
 */
const getSymbolForStyle = (currencyCode: string, options: FormatOptions): string => {
  if (options.symbol) return options.symbol
  if (options.style === 'none') return ''
  if (options.style === 'code') return ` ${currencyCode}`

  return CURRENCY_SYMBOLS[currencyCode as keyof typeof CURRENCY_SYMBOLS] ?? currencyCode
}

// ===================================================================
// ROUNDING
//
// The application has ONE money-rounding rule: half-up, away from zero
// (matching the currency.js factory's negative-safe behaviour above and the
// backend Brick\Money HALF_UP). Never hand-roll Math.round on a monetary
// amount — it rounds toward +∞ (Math.round(-2.5) === -2) and silently
// diverges for negatives. Route every monetary rounding through the two
// primitives below so client/server totals reconcile and rounding stays
// consistent across every domain (invoices, discounts, bills, …).
// ===================================================================

/**
 * Integer division rounded half-up away from zero, using BigInt so ties are
 * exact (IEEE-754 floats cannot represent every decimal tie). This is THE
 * canonical primitive for minor-unit arithmetic — the discount engine and any
 * document calculator share it to guarantee parity with the backend.
 */
const roundedDivide = (numerator: bigint, denominator: bigint): bigint => {
  if (denominator === 0n) return 0n

  const quotient = numerator / denominator
  const remainder = numerator % denominator
  if (remainder === 0n) return quotient

  const absRemainder = remainder < 0n ? -remainder : remainder
  const absDenominator = denominator < 0n ? -denominator : denominator
  if (absRemainder * 2n < absDenominator) return quotient

  const isNegative = numerator < 0n !== denominator < 0n
  return isNegative ? quotient - 1n : quotient + 1n
}

/**
 * Rounds a (possibly fractional) number half-up away from zero — the float
 * counterpart of roundedDivide for the cases where an exact integer
 * numerator/denominator is not available (dividing by a fractional quantity,
 * scaling a human-entered value). Sign-symmetric, unlike native Math.round.
 */
const roundAwayFromZero = (value: number): number => {
  if (!Number.isFinite(value)) return 0
  return Math.sign(value) * Math.round(Math.abs(value))
}

/**
 * Calculates a scaled amount based on the scale difference
 *
 * @param amount - The amount to scale
 * @param scaleDiff - The difference in scale (positive means increase precision, negative means decrease)
 * @returns The scaled amount
 */
const calculateScaledAmount = (amount: number, scaleDiff: number): number => {
  if (scaleDiff > 0) return amount * Math.pow(10, scaleDiff)

  // Scale-down rounds half-up away from zero (the canonical money rule),
  // not Math.round which would round negatives toward +∞.
  return Number(roundedDivide(BigInt(amount), 10n ** BigInt(Math.abs(scaleDiff))))
}

// ===================================================================
// SCALE CONVERSION FUNCTIONS
// ===================================================================

/**
 * Converts a money model to a different scale (precision)
 *
 * @param params - Object containing the money model and target scale
 * @param params.money - The money model to convert
 * @param params.targetScale - The target scale (number of decimal places)
 * @returns A new money model with the same value but different scale
 */
const convertToScale = ({ money, targetScale }: ConvertToScaleParams): MoneyModel => {
  if (money.scale === targetScale) return money

  const scaleDiff = targetScale - money.scale
  const newAmount = calculateScaledAmount(money.amount, scaleDiff)

  return {
    amount: newAmount,
    currency: money.currency,
    scale: targetScale
  }
}

/**
 * Rounds a money model to the places its currency has, half up away from zero, so a four-place cost shows as
 * two places of a euro. The `precision` format option cannot stand in: it rounds a negative half toward zero.
 */
const roundToCurrencyScale = ({ money }: RoundToCurrencyScaleParams): MoneyModel =>
  convertToScale({ money, targetScale: getCurrencyScale(money.currency) })

// ===================================================================
// CREATION FUNCTIONS
// ===================================================================

/**
 * Creates a money model from a major value (e.g., dollars, euros)
 *
 * @param params - Object containing the value and optional currency and scale
 * @param params.value - The major value (e.g., 10.99)
 * @param params.currencyCode - The currency code (defaults to system default)
 * @param params.scale - The scale/precision (defaults to system default)
 * @returns A new money model
 */
const fromMajor = ({ value, currencyCode, scale }: FromMajorParams): MoneyModel => {
  if (!Number.isFinite(value)) {
    return zero({ currencyCode, scale })
  }

  const { currency, scale: finalScale } = getDefaultValues(currencyCode, scale)
  const currencyObj = createCurrencyObject(value, currency, finalScale)

  return {
    amount: currencyObj.intValue,
    currency,
    scale: finalScale
  }
}

/**
 * Creates a money model from a minor value (e.g., cents, pennies)
 *
 * @param params - Object containing the minor amount and optional currency and scale
 * @param params.minorAmount - The minor value (e.g., 1099 for $10.99)
 * @param params.currencyCode - The currency code (defaults to system default)
 * @param params.scale - The scale/precision (defaults to system default)
 * @returns A new money model
 */
const fromMinor = ({ minorAmount, currencyCode, scale }: FromMinorParams): MoneyModel => {
  if (!Number.isFinite(minorAmount)) {
    return zero({ currencyCode, scale })
  }

  const { currency, scale: finalScale } = getDefaultValues(currencyCode, scale)

  return {
    amount: roundAwayFromZero(minorAmount),
    currency,
    scale: finalScale
  }
}

/**
 * Creates a money model from a string representing a minor value
 *
 * @param params - Object containing the minor string and optional currency and scale
 * @param params.minorString - The string representing a minor value (e.g., "1099" for $10.99)
 * @param params.currencyCode - The currency code (defaults to system default)
 * @param params.scale - The scale/precision (defaults to system default)
 * @returns A new money model
 */
const fromMinorString = ({ minorString, currencyCode, scale }: FromMinorStringParams): MoneyModel => {
  if (!minorString || minorString === '') {
    return zero({ currencyCode, scale })
  }

  const minorAmount = parseFloat(minorString)
  if (!Number.isFinite(minorAmount)) {
    return zero({ currencyCode, scale })
  }

  return fromMinor({ minorAmount, currencyCode, scale })
}

/**
 * Creates a zero-value money model
 *
 * @param params - Object containing optional currency and scale
 * @param params.currencyCode - The currency code (defaults to system default)
 * @param params.scale - The scale/precision (defaults to system default)
 * @returns A new money model with zero value
 */
const zero = ({ currencyCode, scale }: ZeroParams = {}): MoneyModel => {
  const { currency, scale: finalScale } = getDefaultValues(currencyCode, scale)

  return {
    amount: 0,
    currency,
    scale: finalScale
  }
}

// ===================================================================
// CONVERSION FUNCTIONS
// ===================================================================

/**
 * Converts a money model to its major value representation (e.g., dollars, euros)
 *
 * @param params - Object containing the money model
 * @param params.money - The money model to convert
 * @returns The major value as a number (e.g., 10.99)
 */
const toMajor = ({ money }: ToMajorParams): number => {
  const majorValue = money.amount / Math.pow(10, money.scale)
  const currencyObj = createCurrencyObject(majorValue, money.currency, money.scale)

  return currencyObj.value
}

/**
 * Gets the minor value (e.g., cents, pennies) from a money model
 *
 * @param params - Object containing the money model
 * @param params.money - The money model
 * @returns The minor value as a number (e.g., 1099 for $10.99)
 */
const toMinor = ({ money }: ToMinorParams): number => {
  return money.amount
}

/**
 * Converts a money model to a string representation of its minor value
 *
 * @param params - Object containing the money model
 * @param params.money - The money model
 * @returns The minor value as a string (e.g., "1099" for $10.99)
 */
const toMinorString = ({ money }: ToMinorStringParams): string => {
  return money.amount.toString()
}

// ===================================================================
// FORMATTING FUNCTIONS
// ===================================================================

/**
 * Formats a money model as a string according to the provided options
 *
 * @param params - Object containing the money model and formatting options
 * @param params.money - The money model to format
 * @param params.options - Formatting options
 * @returns A formatted string representation of the money value
 */
const format = ({ money, options = {} }: FormatParams): string => {
  const majorValue = toMajor({ money })
  const config = createFormatConfig(money, options)
  const currencyObj = currencyJs(majorValue, config)

  return currencyObj.format()
}

/**
 * Formats a money model as a string without any currency symbol or code
 *
 * @param params - Object containing the money model and formatting options
 * @param params.money - The money model to format
 * @param params.options - Formatting options (excluding style)
 * @returns A formatted string with just the amount (e.g., "10.99")
 */
const formatAmount = ({ money, options = {} }: FormatAmountParams): string => {
  return format({ money, options: { ...options, style: 'none' } })
}

/**
 * Formats a money model as a string with a currency symbol
 *
 * @param params - Object containing the money model and formatting options
 * @param params.money - The money model to format
 * @param params.options - Formatting options (excluding style)
 * @returns A formatted string with currency symbol (e.g., "$10.99")
 */
const formatWithSymbol = ({ money, options = {} }: FormatWithSymbolParams): string => {
  return format({ money, options: { ...options, style: 'symbol' } })
}

/**
 * Formats a money model as a string with a currency code
 *
 * @param params - Object containing the money model and formatting options
 * @param params.money - The money model to format
 * @param params.options - Formatting options (excluding style)
 * @returns A formatted string with currency code (e.g., "10.99 USD")
 */
const formatWithCode = ({ money, options = {} }: FormatWithCodeParams): string => {
  return format({ money, options: { ...options, style: 'code' } })
}

/**
 * Formats a money model according to the specified locale
 *
 * @param params - Object containing the money model, locale, and formatting options
 * @param params.money - The money model to format
 * @param params.locale - The locale to use for formatting (e.g., 'en-US', 'de-DE')
 * @param params.options - Formatting options (excluding locale)
 * @returns A locale-specific formatted string
 */
const formatForLocale = ({ money, locale, options = {} }: FormatForLocaleParams): string => {
  return format({ money, options: { ...options, locale } })
}

/**
 * Formats a zero amount with the default currency symbol
 *
 * @param params - Optional object containing currency code, scale, and formatting options
 * @param params.currencyCode - The currency code (defaults to system default)
 * @param params.scale - The scale/precision (defaults to system default)
 * @param params.options - Formatting options (excluding style)
 * @returns A formatted string representing zero (e.g., "€0.00")
 */
const formatZeroAmount = ({ currencyCode, scale, options = {} }: FormatZeroAmountParams = {}): string => {
  const zeroMoney = zero({ currencyCode, scale })
  return formatWithSymbol({ money: zeroMoney, options })
}

/**
 * Formats a money model with symbol, or returns a fallback string if money is null/undefined
 *
 * @param params - Object containing the money model, fallback string, and formatting options
 * @param params.money - The money model to format (can be null or undefined)
 * @param params.fallback - The fallback string to return if money is null/undefined (defaults to '-')
 * @param params.options - Formatting options (excluding style)
 * @returns A formatted string with currency symbol (e.g., "$10.99") or the fallback string
 */
const formatOrDash = ({ money, fallback = PLACEHOLDERS.DASH, options = {} }: FormatOrDashParams): string => {
  if (!money) return fallback
  return formatWithSymbol({ money, options })
}

/**
 * Formats a money model, falling back to a formatted zero when null/undefined.
 * Prefers the model's own pre-formatted string (`money.formatted`) when present.
 *
 * @param params - Object containing the money model and formatting options
 * @param params.money - The money model to format (can be null or undefined)
 * @param params.options - Formatting options (excluding style)
 * @returns A formatted string with currency symbol, or a formatted zero (e.g. "€0.00")
 */
const formatOrZero = ({ money, options = {} }: FormatOrZeroParams): string => {
  if (!money) return formatZeroAmount({ options })
  return money.formatted ?? formatWithSymbol({ money, options })
}

/**
 * Formats a minor-unit (cents) amount with currency symbol, or returns a
 * fallback when the amount is null/undefined. The canonical cents → currency
 * path — use this instead of hand-rolling `formatWithSymbol({ money: fromMinor(...) })`.
 *
 * @param params.minorAmount - Amount in minor units (e.g. cents), or null/undefined
 * @param params.fallback - Returned when minorAmount is null/undefined (defaults to '-')
 * @returns A formatted string with symbol (e.g. "$10.99") or the fallback
 */
const formatMinorOrDash = ({
  minorAmount,
  fallback = PLACEHOLDERS.DASH,
  currencyCode,
  scale,
  options = {}
}: FormatMinorOrDashParams): string => {
  if (minorAmount === null || minorAmount === undefined) return fallback
  return formatWithSymbol({ money: fromMinor({ minorAmount, currencyCode, scale }), options })
}

/**
 * Formats a minor-unit (cents) amount with a currency symbol. The sanctioned replacement for the
 * old `priceUtil.toCurrency` — no more hand-rolled `/ 100` and string-concatenated symbol.
 *
 * @param params.minorAmount - Amount in minor units (e.g. cents)
 * @param params.currencyCode - The currency code (defaults to 'EUR')
 * @param params.scale - The scale/precision (defaults to 2)
 * @returns A formatted string with symbol (e.g. "€10.99")
 */
const formatMinorAmount = ({ minorAmount, currencyCode = 'EUR', scale = 2 }: FormatMinorAmountParams): string => {
  return formatWithSymbol({ money: fromMinor({ minorAmount, currencyCode, scale }) })
}

/**
 * Formats a major-unit (e.g. euros) amount with a currency symbol. The sanctioned replacement for
 * the old `priceUtil.formatCurrency` — no more hand-rolled `toFixed` and string-concatenated symbol.
 *
 * @param params.value - The major value (e.g. 10.99)
 * @param params.currencyCode - The currency code (defaults to 'EUR')
 * @param params.scale - The scale/precision (defaults to 2)
 * @returns A formatted string with symbol (e.g. "€10.99")
 */
const formatMajorAmount = ({ value, currencyCode = 'EUR', scale = 2 }: FormatMajorAmountParams): string => {
  return formatWithSymbol({ money: fromMajor({ value, currencyCode, scale }) })
}

/**
 * Formats an array of money models
 *
 * @param params - Object containing the array of money models and formatting options
 * @param params.moneyArray - Array of money models to format
 * @param params.options - Formatting options
 * @returns Array of formatted strings
 */
const formatMany = ({ moneyArray, options = {} }: FormatManyParams): string[] => {
  return moneyArray.map(money => format({ money, options }))
}

/**
 * Adds a formatted string representation to a money model
 *
 * @param params - Object containing the money model and formatting options
 * @param params.money - The money model
 * @param params.options - Formatting options
 * @returns A new money model with an additional 'formatted' property
 */
const withFormatted = ({ money, options = {} }: WithFormattedParams): MoneyModel => {
  return {
    ...money,
    formatted: format({ money, options })
  }
}

// ===================================================================
// ARITHMETIC OPERATIONS
// ===================================================================

// Whole amounts at one scale add and subtract exactly as integers, with no trip through currency.js.
const isWholeAtOneScale = (money1: MoneyModel, money2: MoneyModel, amount: number): boolean =>
  money1.scale === money2.scale && [money1.amount, money2.amount, amount].every(Number.isSafeInteger)

/**
 * Adds two money models together
 *
 * @param params - Object containing the two money models to add
 * @param params.money1 - First money model
 * @param params.money2 - Second money model
 * @returns A new money model representing the sum
 * @throws Error if the currencies don't match
 */
const add = ({ money1, money2 }: AddParams): MoneyModel => {
  if (money1.currency !== money2.currency) {
    throw new Error(`Cannot add ${money1.currency} and ${money2.currency}`)
  }

  const sum = money1.amount + money2.amount

  if (isWholeAtOneScale(money1, money2, sum)) return { amount: sum, currency: money1.currency, scale: money1.scale }

  const resultScale = Math.max(money1.scale, money2.scale)

  const major1 = toMajor({ money: convertToScale({ money: money1, targetScale: resultScale }) })
  const major2 = toMajor({ money: convertToScale({ money: money2, targetScale: resultScale }) })

  const currency1 = createCurrencyObject(major1, money1.currency, resultScale)
  const currency2 = createCurrencyObject(major2, money1.currency, resultScale)
  const result = currency1.add(currency2)

  if (!Number.isFinite(result.intValue)) {
    return zero({ currencyCode: money1.currency, scale: resultScale })
  }

  return {
    amount: result.intValue,
    currency: money1.currency,
    scale: resultScale
  }
}

/**
 * Subtracts the second money model from the first
 *
 * @param params - Object containing the two money models for subtraction
 * @param params.money1 - Money model to subtract from
 * @param params.money2 - Money model to subtract
 * @returns A new money model representing the difference
 * @throws Error if the currencies don't match
 */
const subtract = ({ money1, money2 }: SubtractParams): MoneyModel => {
  if (money1.currency !== money2.currency) {
    throw new Error(`Cannot subtract ${money2.currency} from ${money1.currency}`)
  }

  const difference = money1.amount - money2.amount

  if (isWholeAtOneScale(money1, money2, difference)) {
    return { amount: difference, currency: money1.currency, scale: money1.scale }
  }

  const resultScale = Math.max(money1.scale, money2.scale)

  const major1 = toMajor({ money: convertToScale({ money: money1, targetScale: resultScale }) })
  const major2 = toMajor({ money: convertToScale({ money: money2, targetScale: resultScale }) })

  const currency1 = createCurrencyObject(major1, money1.currency, resultScale)
  const currency2 = createCurrencyObject(major2, money1.currency, resultScale)
  const result = currency1.subtract(currency2)

  if (!Number.isFinite(result.intValue)) {
    return zero({ currencyCode: money1.currency, scale: resultScale })
  }

  return {
    amount: result.intValue,
    currency: money1.currency,
    scale: resultScale
  }
}

/**
 * Multiplies a money model by a number
 *
 * @param params - Object containing the money model and multiplier
 * @param params.money - The money model to multiply
 * @param params.multiplier - The multiplier
 * @returns A new money model representing the product
 */
const multiply = ({ money, multiplier }: MultiplyParams): MoneyModel => {
  if (!Number.isFinite(multiplier)) {
    return zero({ currencyCode: money.currency, scale: money.scale })
  }

  const majorValue = toMajor({ money })
  const currencyObj = createCurrencyObject(majorValue, money.currency, money.scale)
  const result = currencyObj.multiply(multiplier)

  if (!Number.isFinite(result.intValue)) {
    return zero({ currencyCode: money.currency, scale: money.scale })
  }

  return {
    amount: result.intValue,
    currency: money.currency,
    scale: money.scale
  }
}

/**
 * Divides a money model by a number
 *
 * @param params - Object containing the money model and divisor
 * @param params.money - The money model to divide
 * @param params.divisor - The divisor
 * @returns A new money model representing the quotient
 */
const divide = ({ money, divisor }: DivideParams): MoneyModel => {
  if (!Number.isFinite(divisor) || divisor === 0) {
    return zero({ currencyCode: money.currency, scale: money.scale })
  }

  const majorValue = toMajor({ money })
  const currencyObj = createCurrencyObject(majorValue, money.currency, money.scale)
  const result = currencyObj.divide(divisor)

  if (!Number.isFinite(result.intValue)) {
    return zero({ currencyCode: money.currency, scale: money.scale })
  }

  return {
    amount: result.intValue,
    currency: money.currency,
    scale: money.scale
  }
}

/**
 * Multiplies a money model by numerator ÷ denominator exactly, then rounds once, half up away
 * from zero — the same as the backend's Brick `multipliedBy(BigRational)`. For a ratio of two whole
 * counts, such as paid ÷ received units, where `multiply` would round a float twice.
 */
const multiplyByRatio = ({ money, numerator, denominator }: MultiplyByRatioParams): MoneyModel => ({
  amount: Number(roundedDivide(BigInt(money.amount) * numerator, denominator)),
  currency: money.currency,
  scale: money.scale
})

/**
 * How much of `whole` is `part`, as a whole percent rounded down: 995 of 1,000 reads 99, never 100.
 * It is exact, because it divides whole minor units. A zero whole holds no share, so it reads 0.
 * Meant for two amounts that are not negative: a negative amount divides toward zero instead.
 *
 * @param params.part - The amount that is a share of the whole
 * @param params.whole - The whole it is a share of
 * @returns The percent as a whole number, rounded toward zero
 * @throws Error if the currencies don't match
 */
const percentOf = ({ part, whole }: PercentOfParams): number => {
  if (part.currency !== whole.currency) {
    throw new Error(`Cannot take ${part.currency} as a share of ${whole.currency}`)
  }

  const targetScale = Math.max(part.scale, whole.scale)
  const partMinor = BigInt(convertToScale({ money: part, targetScale }).amount)
  const wholeMinor = BigInt(convertToScale({ money: whole, targetScale }).amount)

  if (wholeMinor === 0n) return 0

  return Number((partMinor * 100n) / wholeMinor)
}

/**
 * Distributes a money amount into equal parts
 *
 * @param params - Object containing the money model and number of parts
 * @param params.money - The money model to distribute
 * @param params.parts - Number of parts to distribute into
 * @returns An array of money models representing the distributed parts
 * @throws Error if parts is not a positive number
 */
const distribute = ({ money, parts }: DistributeParams): MoneyModel[] => {
  if (!Number.isFinite(parts) || parts <= 0) {
    throw new Error('Parts must be a positive number')
  }

  const majorValue = toMajor({ money })
  const currencyObj = createCurrencyObject(majorValue, money.currency, money.scale)
  const distributed = currencyObj.distribute(parts)

  return distributed.map(curr => ({
    amount: curr.intValue,
    currency: money.currency,
    scale: money.scale
  }))
}

/**
 * Splits a money amount by whole weights, as the backend's `Money::allocate()` (Brick) does. Each
 * part is floored, and the leftover minor units go one at a time to the FIRST parts. The parts always
 * add up to the amount, so a split here matches the same split on the server.
 */
const allocate = ({ money, ratios }: AllocateParams): MoneyModel[] => {
  const total = ratios.reduce((carry, ratio) => carry + ratio, 0n)
  if (total <= 0n || ratios.some(ratio => ratio < 0n)) {
    throw new Error('Ratios must be non-negative, and at least one above zero')
  }

  const amount = BigInt(money.amount)
  const parts = ratios.map(ratio => (amount * ratio) / total)
  let remainder = amount - parts.reduce((carry, part) => carry + part, 0n)
  const unit = amount < 0n ? -1n : 1n

  return parts.map(part => {
    if (remainder === 0n) return { amount: Number(part), currency: money.currency, scale: money.scale }

    remainder -= unit
    return { amount: Number(part + unit), currency: money.currency, scale: money.scale }
  })
}

/**
 * Sums an array of money models
 *
 * @param params - Object containing the array of money models to sum
 * @param params.moneyArray - Array of money models to sum
 * @returns A new money model representing the sum
 * @throws Error if the array is empty or contains different currencies
 */
const sum = ({ moneyArray }: SumParams): MoneyModel => {
  if (moneyArray.length === 0) {
    throw new Error('Cannot sum empty array')
  }

  if (moneyArray.length === 1) {
    return moneyArray[0]
  }

  const firstMoney = moneyArray[0]
  const currency = firstMoney.currency

  // Validate all currencies match
  for (const money of moneyArray) {
    if (money.currency !== currency) {
      throw new Error(`Cannot sum different currencies: ${currency} and ${money.currency}`)
    }
  }

  return moneyArray.reduce((accumulator, current) => add({ money1: accumulator, money2: current }))
}

/**
 * Safely sums an array of money models, returning zero if empty
 *
 * @param params - Object containing the array of money models and fallback currency/scale
 * @param params.moneyArray - Array of money models to sum
 * @param params.currencyCode - Currency code for zero fallback (defaults to system default)
 * @param params.scale - Scale for zero fallback (defaults to system default)
 * @returns A new money model representing the sum, or zero if array is empty
 *
 * @remarks
 * Unlike `sum`, this function handles empty arrays gracefully by returning
 * a zero MoneyModel instead of throwing an error. Useful for aggregation
 * scenarios where an empty array is a valid input.
 */
const safeSum = ({ moneyArray, currencyCode, scale }: SafeSumParams): MoneyModel => {
  if (moneyArray.length === 0) {
    return zero({ currencyCode, scale })
  }

  return moneyArray.reduce(
    (accumulator, current) => add({ money1: accumulator, money2: current }),
    zero({ currencyCode: moneyArray[0].currency, scale: moneyArray[0].scale })
  )
}

// ===================================================================
// UTILITY FUNCTIONS
// ===================================================================

/**
 * Checks if a money model has a zero value
 *
 * @param params - Object containing the money model to check
 * @param params.money - The money model to check
 * @returns True if the amount is zero, false otherwise
 */
const isZero = ({ money }: IsZeroParams): boolean => money.amount === 0

/**
 * Checks if a money model has a positive value
 *
 * @param params - Object containing the money model to check
 * @param params.money - The money model to check
 * @returns True if the amount is greater than zero, false otherwise
 */
const isPositive = ({ money }: IsPositiveParams): boolean => money.amount > 0

/**
 * Checks if a money model has a negative value
 *
 * @param params - Object containing the money model to check
 * @param params.money - The money model to check
 * @returns True if the amount is less than zero, false otherwise
 */
const isNegative = ({ money }: IsNegativeParams): boolean => money.amount < 0

/**
 * The size of a money model with its sign dropped, in the same currency and scale.
 * Pair it with a sign word or arrow, so a change reads "+€0.50" instead of "-€0.50".
 *
 * @param params - Object containing the money model
 * @param params.money - The money model to take the size of
 * @returns A new money model with a non-negative amount
 */
const abs = ({ money }: AbsParams): MoneyModel => ({
  amount: Math.abs(money.amount),
  currency: money.currency,
  scale: money.scale
})

/**
 * Checks if two money models are equal in value
 *
 * @param params - Object containing the two money models to compare
 * @param params.money1 - First money model
 * @param params.money2 - Second money model
 * @returns True if the money models have the same currency and value, false otherwise
 */
const equals = ({ money1, money2 }: EqualsParams): boolean => {
  if (money1.currency !== money2.currency) return false

  const resultScale = Math.max(money1.scale, money2.scale)
  const converted1 = convertToScale({ money: money1, targetScale: resultScale })
  const converted2 = convertToScale({ money: money2, targetScale: resultScale })

  return converted1.amount === converted2.amount
}

/**
 * Compares two money models
 *
 * @param params - Object containing the two money models to compare
 * @param params.money1 - First money model
 * @param params.money2 - Second money model
 * @returns -1 if money1 < money2, 0 if money1 = money2, 1 if money1 > money2
 * @throws Error if the currencies don't match
 */
const compare = ({ money1, money2 }: CompareParams): number => {
  if (money1.currency !== money2.currency) {
    throw new Error(`Cannot compare ${money1.currency} and ${money2.currency}`)
  }

  const resultScale = Math.max(money1.scale, money2.scale)
  const converted1 = convertToScale({ money: money1, targetScale: resultScale })
  const converted2 = convertToScale({ money: money2, targetScale: resultScale })

  if (converted1.amount < converted2.amount) return -1
  if (converted1.amount > converted2.amount) return 1

  return 0
}

/**
 * Type guard to check if a value is a valid MoneyModel
 *
 * @param params - Object containing the value to check
 * @param params.value - The value to check
 * @returns True if the value is a valid MoneyModel, false otherwise
 */
const isValidMoney = ({ value }: IsValidMoneyParams) => {
  if (
    value === null ||
    typeof value !== 'object' ||
    !('amount' in value) ||
    !('currency' in value) ||
    !('scale' in value)
  ) {
    return false
  }

  const typedValue = value as Record<string, unknown>

  return (
    typeof typedValue.amount === 'number' &&
    typeof typedValue.currency === 'string' &&
    typeof typedValue.scale === 'number' &&
    Number.isFinite(typedValue.amount) &&
    Number.isFinite(typedValue.scale) &&
    typedValue.scale >= 0 &&
    typedValue.scale <= 8
  )
}

// Rounding Primitives
export { roundedDivide, roundAwayFromZero }

// Scale Conversion Functions
export { convertToScale, roundToCurrencyScale }

// Creation Functions
export { fromMajor, fromMinor, fromMinorString, zero }

// Conversion Functions
export { toMajor, toMinor, toMinorString }

// Formatting Functions
export {
  format,
  formatAmount,
  formatWithSymbol,
  formatWithCode,
  formatForLocale,
  formatZeroAmount,
  formatOrDash,
  formatOrZero,
  formatMinorOrDash,
  formatMinorAmount,
  formatMajorAmount,
  formatMany,
  withFormatted
}

// Arithmetic Operations
export { add, subtract, multiply, multiplyByRatio, percentOf, divide, distribute, allocate, sum, safeSum }

// Utility Functions
export { isZero, isPositive, isNegative, abs, equals, compare, isValidMoney }
